import axios from "axios";

const SESSION_STORAGE_KEY = "token_store";
const SESSION_TIMEOUT_MINUTES = Number(
  process.env.REACT_APP_SESSION_TIMEOUT_MINUTES || 60
);
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;
const SESSION_EXPIRED_EVENT = "wifiads:session-expired";

let axiosConfigured = false;

function getStoredSession() {
  try {
    const sessionValue = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const persistentValue = localStorage.getItem(SESSION_STORAGE_KEY);
    const rawValue = sessionValue || persistentValue;

    // Legacy screens still read token_store directly from sessionStorage.
    // Keep a tab-local mirror when a remembered session is restored.
    if (!sessionValue && persistentValue) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, persistentValue);
    }

    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
}

function isPersistentSession() {
  return Boolean(localStorage.getItem(SESSION_STORAGE_KEY));
}

function saveStoredSession(sessionData, remember = isPersistentSession()) {
  const serializedSession = JSON.stringify(sessionData);

  sessionStorage.setItem(SESSION_STORAGE_KEY, serializedSession);

  if (remember) {
    localStorage.setItem(SESSION_STORAGE_KEY, serializedSession);
  } else {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

export function hasStoredSession() {
  return Boolean(getStoredSession());
}

export function getSessionData() {
  return getStoredSession();
}

export function persistSessionToken(tokenData, remember = false) {
  if (!tokenData) {
    return;
  }

  saveStoredSession(
    {
      ...tokenData,
      sessionActivityAt: Date.now(),
    },
    remember
  );
}

export function updateStoredSession(updates) {
  const currentSession = getStoredSession();

  if (!currentSession) {
    return null;
  }

  const updatedSession = {
    ...currentSession,
    ...updates,
  };

  saveStoredSession(updatedSession);
  return updatedSession;
}

export function clearStoredSession() {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export function markSessionActivity() {
  const currentSession = getStoredSession();

  if (!currentSession) {
    return;
  }

  saveStoredSession({
    ...currentSession,
    sessionActivityAt: Date.now(),
  });
}

export function isSessionExpired() {
  const currentSession = getStoredSession();

  if (!currentSession) {
    return false;
  }

  if (!currentSession.sessionActivityAt) {
    markSessionActivity();
    return false;
  }

  return (
    Date.now() - Number(currentSession.sessionActivityAt) >= SESSION_TIMEOUT_MS
  );
}

export function hasActiveSession() {
  if (!hasStoredSession()) {
    return false;
  }

  if (isSessionExpired()) {
    expireSession();
    return false;
  }

  return true;
}

export function expireSession() {
  clearStoredSession();
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));

  if (!window.location.pathname.startsWith("/auth/sign-in")) {
    window.location.assign("/auth/sign-in");
  }
}

function attachSessionInterceptors(instance) {
  instance.interceptors.request.use(
    (config) => {
      if (hasStoredSession() && isSessionExpired()) {
        expireSession();

        return Promise.reject(new axios.Cancel("Session expired"));
      }

      if (hasStoredSession()) {
        markSessionActivity();
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      if (hasStoredSession()) {
        markSessionActivity();
      }

      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        expireSession();
      }

      return Promise.reject(error);
    }
  );
}

export function configureAxiosSessionHandling() {
  if (axiosConfigured) {
    return;
  }

  attachSessionInterceptors(axios);

  const originalCreate = axios.create.bind(axios);
  axios.create = (...args) => {
    const instance = originalCreate(...args);
    attachSessionInterceptors(instance);
    return instance;
  };

  axiosConfigured = true;
}

export function getSessionExpiredEventName() {
  return SESSION_EXPIRED_EVENT;
}
